import { useEffect, useState } from "react";
import {
	FormProvider,
	SubmitErrorHandler,
	SubmitHandler,
	useController,
	useForm,
	useFormState,
	useWatch
} from "react-hook-form";
import { Tabs, TabsContent } from "src/components/ui/tabs";
import BannerTypePicker from "../../../HomepageCarouselBanners/containers/BannerTypePicker";
import { ArrowLeft, AlertTriangle, RefreshCw, Eye } from "lucide-react";
import { Button } from "src/components/ui/button";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { Link } from "react-router-dom";
import isEmpty from "lodash.isempty";
import BannerStepList from "../../../HomepageCarouselBanners/containers/BannerStepList/BannerStepList";
import useHashtag from "../../../HomepageCarouselBanners/hooks/useHashtag";
import useCreateHashtag from "../../../HomepageCarouselBanners/hooks/useCreateHashtag";
import GroupsAudienceCard from "src/components/GroupsAudienceCard/GroupsAudienceCard";
import LinkPickerStep from "src/components/LinkPicker/LinkPickerStep";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

/**
 * Audience picker sub-component. Mounted only when the parent passes
 * `showAudienceGroups`, so the `useController` registration for
 * `audienceGroupIds` is gated on consumer opt-in. This avoids non-opt-in
 * consumers (e.g. homepage-carousel, social-feed-adverts) silently
 * submitting an empty `audience_group_ids` and wiping backend audience on
 * save.
 */
const AudienceGroupsSection = () => {
	const { field } = useController<{ audienceGroupIds?: number[] }>({
		name: "audienceGroupIds",
		defaultValue: []
	});
	const {
		data: adminGroups = [],
		isLoading: groupsLoading,
		isError: groupsError
	} = useAdminGroups();

	return (
		<div className="mt-8">
			<GroupsAudienceCard
				groupIds={
					Array.isArray(field.value)
						? field.value
						: field.value !== null && field.value !== undefined
							? [field.value]
							: []
				}
				onChange={field.onChange}
				groups={adminGroups.map((g) => ({
					id: Number(g.id),
					name: g.name
				}))}
				isLoading={groupsLoading}
				isError={groupsError}
			/>
		</div>
	);
};

export interface CreateBannerProps {
	onSubmit: SubmitHandler<BannerFormState>;
	defaultValues?: BannerFormState;
	isSubmitting?: boolean;
	bannerTypeRadioOptions?: BannerType[];
	showVariantTab?: boolean;
	showBannerTypePicker?: boolean;
	/**
	 * When true, render the Groups Audience Card below the form fields and
	 * wire `audienceGroupIds` into form state. Opt-in so existing form
	 * consumers (full-screen-takeover, social-feed-adverts) keep their
	 * current layout until each feature opts in.
	 */
	showAudienceGroups?: boolean;
	root:
		| "homepage-carousel"
		| "full-screen-takeover"
		| "promotion"
		| "social-feed-adverts";
	backPath?: string;
}

const CreateBannerForm = ({
	onSubmit,
	defaultValues,
	isSubmitting,
	bannerTypeRadioOptions,
	showBannerTypePicker = true,
	showVariantTab = true,
	showAudienceGroups = false,
	root,
	backPath
}: CreateBannerProps) => {
	const methods = useForm<BannerFormState>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { control, setValue, handleSubmit, clearErrors, reset } = methods;

	const { errors, isDirty } = useFormState({ control });

	const [tabIndex, setTabIndex] = useState(0);

	const { bannerType, bannerStyle, selectedHashtag } = useWatch({
		control
	});

	const hashtag = useWatch({
		control,
		name: `steps.${4}.fields.${3}.givenAnswer`
	});

	const { refetch } = useHashtag(hashtag, { enabled: false });

	const { mutate: createHashtag } = useCreateHashtag();

	const handleTabChange = (value: string) => {
		if (value === "campaign") {
			setTabIndex(1);
			setValue("bannerStyle", "campaign");
		} else {
			setTabIndex(0);
			setValue("bannerStyle", "standard");
		}
	};

	const createNewHashtag = async () => {
		const { data } = await refetch();

		if (data && data.results.length && data.results[0].name === hashtag) return;

		if (hashtag && selectedHashtag) {
			createHashtag({ name: hashtag });
		}
	};

	const onInvalidSubmit: SubmitErrorHandler<BannerFormState> = (error) => {
		console.log("FAILED TO SUBMIT FORM:", error);
		window.scrollTo(0, 0);
	};

	const onValidSubmit: SubmitHandler<BannerFormState> = async (data) => {
		if (bannerStyle === "campaign" && !selectedHashtag)
			return onInvalidSubmit(errors);

		createNewHashtag();

		onSubmit(data);
	};

	const handleOnSubmitClick = handleSubmit(onValidSubmit, onInvalidSubmit);

	const isTakeover = bannerType === "fullScreenTakeoverBanner";
	const isPromotion =
		bannerType === "promotionImage" || bannerType === "promotionVideo";
	// LinkPicker replaces the legacy URL-only field for promotions and
	// takeovers, both of which gained `open_in_browser` and `feed_id`
	// columns in the backend.
	const showLinkPicker = isTakeover || isPromotion;

	// Takeover preview watches (layout: 0=name, 1=mobile, 2=tablet, 3=button)
	const takeoverMobileImage = useWatch({
		control,
		name: "steps.1.fields.0.givenAnswer"
	});
	const takeoverButtonText = useWatch({
		control,
		name: "steps.3.fields.1.givenAnswer"
	});
	const takeoverButtonStyle = useWatch({
		control,
		name: "steps.3.fields.0.givenAnswer"
	});
	const takeoverButtonDisabled = useWatch({
		control,
		name: "steps.3.isStepDisabled"
	});

	const video = useWatch({
		control,
		name: `steps.${2}`
	});

	const medias = useWatch({
		control,
		name: `steps.${0}`
	});

	const imagePortrait = useWatch({
		control,
		name: `steps.${1}.fields.${0}`
	});

	const imageLandscape = useWatch({
		control,
		name: `steps.${1}.fields.${1}`
	});

	const isFilesUploading =
		video?.uploading ||
		imagePortrait?.uploading ||
		imageLandscape?.uploading ||
		medias?.uploading;

	const isErrorUploading =
		video?.error ||
		imagePortrait?.error ||
		imageLandscape?.error ||
		medias?.error;

	useEffect(() => {
		clearErrors("steps");
	}, [bannerType, tabIndex]);

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	return (
		<FormProvider {...methods}>
			<form
				onKeyDown={(e) => {
					if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
						e.preventDefault();
					}
				}}
			>
				<Tabs
					value={tabIndex === 1 ? "campaign" : "standard"}
					onValueChange={handleTabChange}
				>
					{showBannerTypePicker && bannerTypeRadioOptions && (
						<BannerTypePicker
							showVariantTab={showVariantTab}
							radioOptions={bannerTypeRadioOptions}
						/>
					)}

					<TabsContent
						value="standard"
						className="w-full px-0 mt-[31px]"
						aria-label="standard-tab"
					>
						<BannerStepList />
					</TabsContent>

					<TabsContent
						value="campaign"
						className="w-full px-0 mt-[31px]"
						aria-label="campaign-tab"
					>
						<BannerStepList />
					</TabsContent>
				</Tabs>

				{/* Takeover Preview */}
				{isTakeover && (
					<div className="mt-8 p-5 bg-primary-lighter rounded-xl border border-primary-light/30">
						<div className="flex items-center gap-2 mb-3 text-xs font-medium uppercase tracking-wider text-primary-dark">
							<Eye className="h-3.5 w-3.5" />
							<span>Preview</span>
						</div>
						<div
							className="rounded-2xl overflow-hidden shadow-md max-w-[240px] relative"
							style={{ aspectRatio: "856/1852" }}
						>
							{takeoverMobileImage ? (
								<div className="relative w-full h-full">
									<AzureImage
										uuid={takeoverMobileImage as string}
										imageProfile="retail-app-full-page-takeover-mobile"
										style={{
											objectFit: "cover",
											width: "100%",
											height: "100%",
											position: "absolute",
											inset: 0
										}}
										alt="Takeover preview"
									/>
									{takeoverButtonText && !takeoverButtonDisabled && (
										<div className="absolute bottom-4 left-0 right-0 flex justify-center">
											<div
												className="px-4 py-1.5 rounded-full text-[10px] font-medium shadow-md"
												style={{
													backgroundColor:
														takeoverButtonStyle === "white"
															? "#ffffff"
															: "#18181b",
													color:
														takeoverButtonStyle === "white"
															? "#18181b"
															: "#ffffff"
												}}
											>
												{takeoverButtonText}
											</div>
										</div>
									)}
								</div>
							) : (
								<div className="flex items-center justify-center bg-muted text-muted-foreground text-sm w-full h-full">
									Upload a mobile image to preview
								</div>
							)}
						</div>
					</div>
				)}

				{/* Link picker — promotions and takeovers store the link via flat
				    form fields rather than via the step-array, so the consolidated
				    LinkPicker renders here as its own card. */}
				{showLinkPicker && (
					<div className="mt-4 rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Link (optional)
							</label>
							<LinkPickerStep
								urlName="buttonLink"
								openInBrowserName="openInBrowser"
								feedName="linkFeedId"
								selectedFeedName="selectedLinkFeed"
								label="Link (optional)"
								helperText="Choose one. Switching options replaces your current selection."
								hideLabel
							/>
						</div>
					</div>
				)}

				{/* Groups audience (opt-in). Rendered as a child component so the
				    useController call only registers `audienceGroupIds` on the form
				    when the picker is actually shown — non-opt-in consumers don't
				    inject an empty array that would clear the backend's audience
				    on save. */}
				{showAudienceGroups && <AudienceGroupsSection />}

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<Link
						to={
							backPath ??
							(root === "social-feed-adverts"
								? "/social-feed/business-posts?published=false"
								: `/vendor-sponsorship/${root}/banners?published=false`)
						}
						className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						aria-label="Back to all banners"
					>
						<ArrowLeft className="h-4 w-4" aria-hidden="true" />
						Back to all banners
					</Link>

					<div className="flex items-center gap-4">
						{isFilesUploading && (
							<div className="flex items-center gap-1.5 text-zinc-600">
								<div className="animate-spin">
									<RefreshCw className="h-4 w-4" />
								</div>
								<span className="text-sm">
									{video.uploading && "Video "}
									{(imagePortrait.uploading || imageLandscape.uploading) &&
										"Image "}
									{medias.uploading && "media(s) "}
									is uploading
								</span>
							</div>
						)}

						{isErrorUploading && (
							<div className="flex items-center gap-1.5 text-red-500">
								<AlertTriangle className="h-4 w-4" />
								<span className="text-sm">
									Problem uploading the {video.error && "video"}
									{(imagePortrait.error || imageLandscape.error) && "image"}
									{medias.error && "media(s)"}. Please try again.
								</span>
							</div>
						)}

						<Button
							type="submit"
							disabled={
								!isDirty ||
								!isEmpty(errors) ||
								isSubmitting ||
								isFilesUploading ||
								isErrorUploading
							}
							onClick={handleOnSubmitClick}
						>
							{isSubmitting ? "Saving..." : "Save to library"}
						</Button>
					</div>
				</div>
			</form>
		</FormProvider>
	);
};

export default CreateBannerForm;
